import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { UserRepository } from '@data/user/user.store';
import { environment } from '../../../environments/environment';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppRouteNames } from '../../app.routing.names';
import { APP_CONFIG } from '@core/config/app-config.token';

export const useridInterceptor: HttpInterceptorFn = (request, next) => {
  const userRepo = inject(UserRepository);
  const router = inject(Router);
  const appConfig = inject(APP_CONFIG);

  // A request without the server-issued token authenticates with the raw user id.
  const sentToken = !!localStorage.getItem('authToken');

  const handleUnauthorizedError = (error: unknown): Observable<HttpEvent<unknown>> => {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    if (error.status === HttpStatusCode.Unauthorized) {
      // Drop the dead session (in-memory user + token) so the guard re-bootstraps and
      // the next request doesn't replay an expired credential. userId is kept so the
      // exchange can restore the same user — unless the raw id itself was refused
      // (AUTH_ENFORCE_TOKENS): retrying it would loop, so the guard starts a new visitor.
      userRepo.clearSession();
      if (!sentToken) {
        localStorage.removeItem('userId');
      }
      router.navigate([AppRouteNames.HOME]);
      // Emit the error into the success channel (legacy behavior owned by
      // anonymous-api-auth); cast preserves it byte-for-byte after the fn migration.
      return of(error) as unknown as Observable<HttpEvent<unknown>>;
    }

    return throwError(() => error);
  };

  const isApiUrl = request.url.startsWith(environment.apiUrl);
  if (isApiUrl) {
    // Tabs share the stored token and id but each keeps its own visitor in memory. Another tab moved
    // the device to a new visitor: this tab's requests carry the old visitor's ids, so none may go out
    // with the new token. The first one drops the old visitor; the rest (same tick, or until the
    // next login) are matched by the dropped id.
    const storedUserId = localStorage.getItem('userId');
    const tabVisitorId = userRepo.user?.id ?? userRepo.droppedVisitorId;
    if (
      tabVisitorId &&
      storedUserId &&
      tabVisitorId !== storedUserId &&
      localStorage.getItem('authToken')
    ) {
      if (userRepo.user) {
        userRepo.dropStaleVisitor();
        router.navigate([AppRouteNames.HOME]);
      }
      return EMPTY;
    }

    // Prefer the server-issued session token; fall back to the raw user id for
    // legacy/dev combinations (dropped once AUTH_ENFORCE_TOKENS is enabled).
    const bearer =
      localStorage.getItem('authToken') ?? userRepo.user?.id ?? localStorage.getItem('userId');
    if (bearer) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${bearer}`,
          'X-Platform': appConfig.app.name,
        },
      });
    }
  }

  return next(request).pipe(catchError((error) => handleUnauthorizedError(error)));
};
